import { useSelector, useDispatch } from "react-redux";
import {
  ProductCreateState,
  actionSetIsEditing,
  actionSetProduct,
  actionResetProduct,
  actionSetName,
  actionSetImage,
  actionSetImageURL,
  actionSetProductType,
  actionSetActive,
  actionSetDescription,
  actionSetSku,
  actionSetUPC,
  actionSetLength,
  actionSetWidth,
  actionSetHeight,
  actionSetWeight,
  actionSetLotProduct,
  actionSetDangerous,
  actionSetFragile,
  actionSetContainsBattery,
  actionSetLiquidProduct,
  actionSetTracksExpiryDate,
  actionSetDaysBeforeExpiryToBlockFulfillment,
  actionSetPackingDetails,
  actionSetTariffCode,
  actionSetCustomsDescription,
  actionSetCost,
  actionSetWholesaleCost,
  actionSetRetailPrice,
  actionSetPackageRequirements,
  actionSetBaseProducts,
  actionSetRequiredParts,
  actionSetReturnDaysConstraint,
  actionSetInsuranceRequired,
  actionSetInsuranceValue,
  actionSetIsService,
  actionSetManufacturingCountry,
} from "../../../ducks/productCreateFlow";
import { Product } from "../../../interfaces/products";

// Interfaces
interface State {
  fulfillment: { productCreateFlow: ProductCreateState };
}

// Selectors
const selectIsEditing = (state: State): boolean => state.fulfillment.productCreateFlow.isEditing;
const selectProduct = (state: State) => state.fulfillment.productCreateFlow.product as Product;

const useProductCreateFlow = () => {
  const dispatch = useDispatch();

  const isEditing = useSelector(selectIsEditing);
  const product = useSelector(selectProduct);

  const dispatchSetIsEditing = (isEditing: ProductCreateState["isEditing"]) => dispatch(actionSetIsEditing(isEditing));
  const dispatchSetProduct = (product: Product) => dispatch(actionSetProduct(product));
  const dispatchResetProduct = () => dispatch(actionResetProduct());
  const dispatchSetName = (name: Product["name"]) => dispatch(actionSetName(name));
  const dispatchSetImage = (image: File | null) => dispatch(actionSetImage(image));
  const dispatchSetImageURL = (imageURL: Product["image_url"]) => dispatch(actionSetImageURL(imageURL));
  const dispatchSetProductType = (productType: Product["product_type"]) => dispatch(actionSetProductType(productType));
  const dispatchSetActive = (active: Product["active"]) => dispatch(actionSetActive(active));
  const dispatchSetDescription = (description: Product["description"]) => dispatch(actionSetDescription(description));
  const dispatchSetSku = (sku: Product["sku"]) => dispatch(actionSetSku(sku));
  const dispatchSetUPC = (upc: Product["upc"]) => dispatch(actionSetUPC(upc));
  const dispatchSetLength = (length: Product["length"]) => dispatch(actionSetLength(length));
  const dispatchSetWidth = (width: Product["width"]) => dispatch(actionSetWidth(width));
  const dispatchSetHeight = (height: Product["height"]) => dispatch(actionSetHeight(height));
  const dispatchSetWeight = (weight: Product["weight"]) => dispatch(actionSetWeight(weight));
  const dispatchSetLotProduct = (isLotProduct: Product["tracks_lot_numbers"]) =>
    dispatch(actionSetLotProduct(isLotProduct));
  const dispatchSetDangerous = (dangerous: Product["dangerous"]) => dispatch(actionSetDangerous(dangerous));
  const dispatchSetFragile = (fragile: Product["fragile"]) => dispatch(actionSetFragile(fragile));
  const dispatchSetContainsBattery = (containsBattery: Product["contains_battery"]) =>
    dispatch(actionSetContainsBattery(containsBattery));
  const dispatchSetLiquidProduct = (liquid: Product["liquid"]) => dispatch(actionSetLiquidProduct(liquid));
  const dispatchSetTracksExpiryDate = (tracksExpiryDate: Product["tracks_expiry_date"]) =>
    dispatch(actionSetTracksExpiryDate(tracksExpiryDate));
  const dispatchSetDaysBeforeExpiryToBlockFulfillment = (
    daysBeforeExpiryToBlockFulfillment: Product["days_before_expiry_to_block_fulfillment"]
  ) => dispatch(actionSetDaysBeforeExpiryToBlockFulfillment(daysBeforeExpiryToBlockFulfillment));
  const dispatchSetPackingDetails = (packingDetails: Product["packaging_levels"]) =>
    dispatch(actionSetPackingDetails(packingDetails));
  const dispatchSetTariffCode = (tariffCode: Product["hs_tariff_code"]) => dispatch(actionSetTariffCode(tariffCode));
  const dispatchSetCustomsDescription = (customsDescription: Product["customs_description"]) =>
    dispatch(actionSetCustomsDescription(customsDescription));
  const dispatchSetCost = (value: Product["cost"]) => dispatch(actionSetCost(value));
  const dispatchSetWholesalePrice = (wholesaleValue: Product["wholesale_price"]) =>
    dispatch(actionSetWholesaleCost(wholesaleValue));
  const dispatchSetRetailPrice = (retailValue: Product["retail_price"]) => dispatch(actionSetRetailPrice(retailValue));
  const dispatchSetPackageRequirements = (packagingCriteria: Product["packaging_criteria"]) =>
    dispatch(actionSetPackageRequirements(packagingCriteria));
  const dispatchSetBaseProducts = (baseProducts: Product["base_products"]) =>
    dispatch(actionSetBaseProducts(baseProducts));
  const dispatchSetRequiredParts = (requiredParts: Product["required_parts"]) =>
    dispatch(actionSetRequiredParts(requiredParts));
  const dispatchSetReturnDaysConstraint = (returnDays: Product["return_days"]) =>
    dispatch(actionSetReturnDaysConstraint(returnDays));
  const dispatchSetInsuranceRequired = (insurance_required: Product["insurance_required"]) =>
    dispatch(actionSetInsuranceRequired(insurance_required));
  const dispatchSetInsuranceValue = (insurance_value: Product["insurance_value"]) =>
    dispatch(actionSetInsuranceValue(insurance_value));
  const dispatchSetIsService = (is_service: Product["is_service"]) => dispatch(actionSetIsService(is_service));
  const dispatchSetManufacturingCountry = (value: Product["manufacturing_country_origin"]) =>
    dispatch(actionSetManufacturingCountry(value));

  return {
    isEditing,
    product,

    dispatchSetIsEditing,
    dispatchSetProduct,
    dispatchResetProduct,
    dispatchSetName,
    dispatchSetImage,
    dispatchSetImageURL,
    dispatchSetProductType,
    dispatchSetActive,
    dispatchSetDescription,
    dispatchSetSku,
    dispatchSetUPC,
    dispatchSetLength,
    dispatchSetWidth,
    dispatchSetHeight,
    dispatchSetWeight,
    dispatchSetLotProduct,
    dispatchSetDangerous,
    dispatchSetFragile,
    dispatchSetContainsBattery,
    dispatchSetLiquidProduct,
    dispatchSetTracksExpiryDate,
    dispatchSetDaysBeforeExpiryToBlockFulfillment,
    dispatchSetPackingDetails,
    dispatchSetTariffCode,
    dispatchSetCustomsDescription,
    dispatchSetCost,
    dispatchSetWholesalePrice,
    dispatchSetRetailPrice,
    dispatchSetPackageRequirements,
    dispatchSetBaseProducts,
    dispatchSetRequiredParts,
    dispatchSetReturnDaysConstraint,
    dispatchSetInsuranceRequired,
    dispatchSetInsuranceValue,
    dispatchSetIsService,
    dispatchSetManufacturingCountry,
  };
};

export default useProductCreateFlow;
